import {
  FilterAltTwoTone,
  PhoneTwoTone,
  VisibilityTwoTone,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { guideTourCustomerPaymentStateList } from '../api';
import { useAuth } from '../hooks/useAuth';
import { ICustomerPaymentStateList } from '../utils/types';
import GeneralCustomerListFilterDialog from './GeneralCustomerListFilterDialog';

type GuideCustomerPaymentStateTableProps = {
  salesIndividualId: string;
};

export default function GuideCustomerPaymentStateTable({
  salesIndividualId,
}: GuideCustomerPaymentStateTableProps) {
  const [customerPaymentState, setCustomerPaymentState] = useState<
    ICustomerPaymentStateList[]
  >([]);
  const { token } = useAuth();
  const { t } = useTranslation();
  const [, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState('');
  const [openFilter, setOpenFilter] = useState(false);

  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>([]);
  const [selectedTourGroups, setSelectedTourGroups] = useState<string[]>([]);
  const [
    selectedSalesIndividualStatusOptions,
    setSelectedSalesIndividualStatusOptions,
  ] = useState<string[]>([]);

  const [visiblePhoneIndex, setVisiblePhoneIndex] = useState<number | null>(
    null,
  );

  const paymentNotCompleted = customerPaymentState.filter(
    (row) => row.balanceAmount > 0 && row.salesIndividualStatus,
  ).length;

  const tourGroupOptions = Array.from(
    new Set(
      customerPaymentState
        .filter((row) => row?.tourGroupName)
        .map((row) => row.tourGroupName),
    ),
  );

  const salesIndividualStatusOptions = [t('guidanceTourPayment.cancelled')];
  const canceledLabel = t('guidanceTourPayment.cancelled');

  const salesGroupOptions = Array.from(
    new Set(
      customerPaymentState
        .filter((row) => row?.salesOrderGroup)
        .map((row) => row.salesOrderGroup),
    ),
  );

  useEffect(() => {
    fetchGuideTourRelatedPaymentStateList(salesIndividualId);
  }, [salesIndividualId]);

  const fetchGuideTourRelatedPaymentStateList = async (
    salesIndividualId: string,
  ) => {
    try {
      const res = await guideTourCustomerPaymentStateList(
        salesIndividualId,
        token,
      );
      const data = await res.json();
      if (res?.status === 200) {
        setCustomerPaymentState(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneIconClick = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleShowNumber = (index: number) => {
    setVisiblePhoneIndex(visiblePhoneIndex === index ? null : index);
  };

  return (
    <Box>
      {loading && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress color="secondary" />
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 900,
              marginTop: '24px',
            }}
          >
            {t('balancesQueryPage.loading')}
          </Typography>
        </Box>
      )}
      {!loading && (
        <>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => setOpenFilter(!openFilter)}
                sx={{ mr: 1 }}
              >
                <FilterAltTwoTone />
              </IconButton>

              <TextField
                label={t('guideTourAccommodationInfo.searchPerson')}
                placeholder={t('guideTourAccommodationInfo.searchWithName')}
                variant="outlined"
                color="secondary"
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon color="secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <Alert severity="error" sx={{ alignItems: 'center', mb: 1, mt: 1 }}>
            {t('guidanceTourPayment.paymentWaitingPax', {
              pax: paymentNotCompleted,
            })}
          </Alert>

          {/* DIALOG FOR FILTER */}
          <GeneralCustomerListFilterDialog
            open={openFilter}
            onClose={() => setOpenFilter(false)}
            tourGroupOptions={tourGroupOptions}
            selectedTourGroups={selectedTourGroups}
            setSelectedTourGroups={setSelectedTourGroups}
            salesGroupOptions={salesGroupOptions}
            selectedSalesGroups={selectedSalesGroups}
            setSelectedSalesGroups={setSelectedSalesGroups}
            salesIndividualStatusOptions={salesIndividualStatusOptions}
            selectedSalesIndividualStatus={selectedSalesIndividualStatusOptions}
            setSelectedSalesIndividualStatus={
              setSelectedSalesIndividualStatusOptions
            }
          />

          <TableContainer component={Paper} sx={{ mt: 1, border: 'none' }}>
            <Table size="small" aria-label="Payment table">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.phoneNumber')}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.customerName')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guidePaymentStateDialog.totalAmount')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guidePaymentStateDialog.paymentAmount')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guidePaymentStateDialog.balanceAmount')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guidePaymentStateDialog.discountAmount')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guidePaymentStateDialog.bedType')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideTransportationDialog.salesOffice')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideTransportationDialog.tourGroup')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customerPaymentState
                  .filter((row) => {
                    const matchesSearch = row.customerFullName
                      .toLowerCase()
                      .includes(searchValue.toLowerCase());

                    const matchesTourGroup =
                      selectedTourGroups.length === 0 ||
                      selectedTourGroups.includes(row.tourGroupName);

                    const matchesSalesGroup =
                      selectedSalesGroups.length === 0 ||
                      selectedSalesGroups.includes(row.salesOrderGroup);

                    const isCancelFilterSelected =
                      selectedSalesIndividualStatusOptions.includes(
                        canceledLabel,
                      );
                    const matchesStatus = isCancelFilterSelected
                      ? !row.salesIndividualStatus
                      : row.salesIndividualStatus;

                    return (
                      matchesSearch &&
                      matchesTourGroup &&
                      matchesSalesGroup &&
                      matchesStatus
                    );
                  })
                  .map((row, index: number) => (
                    <TableRow key={row.salesIndividualId}>
                      {/* PHONE NUMBER & ICONS */}
                      <TableCell
                        sx={{
                          border: (t) => '1px solid ' + t.palette.divider,
                        }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {visiblePhoneIndex === index ? null : (
                            <IconButton
                              size="small"
                              onClick={() =>
                                handlePhoneIconClick(`+${row.fullNumber}`)
                              }
                            >
                              <PhoneTwoTone />
                            </IconButton>
                          )}
                          {visiblePhoneIndex === index ? (
                            <a href={`tel:+${row.fullNumber}`}>
                              <Typography>{row.fullNumber}</Typography>
                            </a>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleShowNumber(index)}
                            >
                              <VisibilityTwoTone />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>

                      {/* CUSTOMER NAME */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="left"
                      >
                        {row.customerFullName}
                      </TableCell>

                      {/* TOTAL AMOUNT */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.totalAmount || '-'}
                      </TableCell>

                      {/* PAYMENT AMOUNT */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.paymentTotal || '-'}
                      </TableCell>

                      {/* BALANCE AMOUNT */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.balanceAmount || '-'}
                      </TableCell>

                      {/* DISCOUNT AMOUNT */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.discountAmount || '-'}
                      </TableCell>

                      {/* BED TYPE */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.bedType || '-'}
                      </TableCell>

                      {/* SALES OFFICE (GROUP) */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.salesOrderGroup || '-'}
                        {row.salesOffice !== row.salesOrderGroup
                          ? `(${row.salesOffice})`
                          : ''}
                      </TableCell>
                      {/* TOUR GROUP NAME */}
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        align="center"
                      >
                        {row.tourGroupName || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
